import type { Farm } from "@/mocks/farms";

/**
 * Alt-text helpers.
 *
 * Editors write real alt text for every photo in the Backend, so that text is
 * always preferred. These helpers only build a descriptive fallback, used when
 * a record has no alt of its own (for example a farm that was never captioned,
 * or the offline mock data).
 */

const clean = (value?: string): string => (value || "").replace(/\s+/g, " ").trim();

/** Alt text for a farm's cover image. */
export function farmCoverAlt(farm: Farm): string {
  const preferred = clean(farm.imageAlt);
  if (preferred) return preferred;

  const region = [farm.village, farm.district].filter(Boolean).join(", ");
  const crop = clean(farm.crop) || "mixed";
  return region
    ? `${farm.name} — ${crop} farm in ${region}, Konaseema`
    : `${farm.name} — ${crop} farm in Konaseema, Andhra Pradesh`;
}

/** Alt text for one photo in a farm's gallery. */
export function farmPhotoAlt(farm: Farm, index: number, total: number): string {
  const preferred = clean(farm.photoAlts?.[index]);
  if (preferred) return preferred;

  const crop = clean(farm.crop) || "farm";
  const position = total > 1 ? `, photo ${index + 1} of ${total}` : "";
  return `${crop} at ${farm.name} in ${farm.village}${position}`;
}

/** Alt text for a visitor photo attached to a review. */
export function reviewPhotoAlt(
  photoAlts: string[] | undefined,
  index: number,
  fallback: string,
): string {
  return clean(photoAlts?.[index]) || fallback;
}